import React from 'react'
import PropTypes from 'prop-types'
import debounce from 'debounce'
import { isObjectEmpty } from 'tools/object'

const noop = () => {}

const keyCodes = {
  TAB: 9,
  ENTER: 13,
  ESC: 27,
  ARROW_UP: 38,
  ARROW_DOWN: 40
}

const trapEnterKey = (e) => e.keyCode === keyCodes.ENTER && e.preventDefault()

const rotateIndex = (current, change, length) => {
  const next = current + change
  if (next >= length) return -1
  if (next < -1) return length - 1
  return next
}

class SearchInput extends React.Component {
  static propTypes = {
    searchApi: PropTypes.func,
    name: PropTypes.string,
    onChange: PropTypes.func,
    onSearchResultsClosed: PropTypes.func,
    onSearchStringUpdate: PropTypes.func,
    onSearchLoading: PropTypes.func,
    onSearchLoaded: PropTypes.func,
    onSearchResultsUpdate: PropTypes.func,
    onSearchResultSelected: PropTypes.func,
    onReset: PropTypes.func,
    debounceDuration: PropTypes.number, // in ms
    onSearchResultsShow: PropTypes.func,
    onSearchResultsClose: PropTypes.func,
    resultParser: PropTypes.func,
    initialSelected: PropTypes.object
  }

  static defaultProps = {
    searchApi: noop,
    onChange: noop,
    renderInput: () => null,
    renderSearchResults: () => null,
    onSearchResultsClosed: noop,
    onSearchStringUpdate: noop,
    onSearchLoading: noop,
    onSearchLoaded: noop,
    onSearchResultsUpdate: noop,
    onSearchResultSelected: noop,
    onReset: noop,
    debounceDuration: 500,
    resultParser: x => x,
    initialSelected: null
  }

  constructor (props) {
    super(props)
    const initialSelected = props.initialSelected
    this.state = {
      showDropdown: false,
      searchString: '',
      loading: false,
      searchResults: [],
      selected: initialSelected,
      getInputProps: this.getInputProps,
      getDropdownProps: this.getDropdownProps,
      getResultItemProps: this.getResultItemProps,
      actions: {
        updateSearchString: this.updateSearchString,
        setSearchLoading: this.setSearchLoading,
        setSearchLoaded: this.setSearchLoaded,
        setSearchResultSelected: this.setSearchResultSelected,
        reset: this.reset,
        search: this.search,
        searchAndSetSelected: this.searchAndSetSelected
      }
    }
  }

  componentDidUpdate (prevProps, prevState) {
    const { selected } = this.state
    if (prevState.selected !== selected) {
      const { name, onChange } = this.props
      onChange(name, selected)
    }
  }

  // savedSearchString is used to save the last searchString that user actually typed, so that when we loop over the search results using arrow up/down keys, we don't lose the original search string.
  // isUserType is used as a flag to identify if the change is due to user typing in the input
  updateSearchString = (searchString, isUserTyping = true) => this.setState((state) => ({ searchString, selected: null, savedSearchString: isUserTyping ? searchString : state.savedSearchString }), () => {
    isUserTyping && this.props.onSearchStringUpdate(this.state)
    isUserTyping && this.debouncedSearch()
  })

  setSearchLoading = () => this.setState({ loading: true }, () => this.props.onSearchLoading(this.state))

  setSearchLoaded = () => this.setState({ loading: false }, () => this.props.onSearchLoaded(this.state))

  updateSearchResults = (searchResults) => this.setState({ searchResults, highlightedIndex: -1 }, () => {
    this.props.onSearchResultsUpdate(this.state)
    this.showDropdown()
  })

  setSearchResultSelected = (selected) => {
    if (!selected) return
    if (typeof selected.fetchDetails === 'function') {
      this.searchAndSetSelected(selected.fetchDetails)
      return
    }
    this.setState({ selected }, () => {
      this.props.onSearchResultSelected(this.state)
      this.closeDropdown()
    })
  }

  reset = () => this.setState({ searchString: '', selected: null, loading: false, searchResults: [] }, () => this.props.onReset(this.state))

  showDropdown = () => this.setState({ showDropdown: true }, this.props.onSearchResultsShow)

  closeDropdown = () => this.setState({ showDropdown: false }, this.props.onSearchResultsClose)

  debouncedSearch = debounce(() => {
    const { searchApi } = this.props
    const { searchString } = this.state
    searchString !== '' && this.search(searchApi)
  }, this.props.debounceDuration)

  searchAndSetSelected = async (searchApi, index = 0) => {
    this.setSearchLoading()
    try {
      const searchResults = await searchApi()
      this.setSearchLoaded()
      const selected = Array.isArray(searchResults) ? searchResults[index] : searchResults
      this.setSearchResultSelected(selected)
      return selected
    } catch (e) {
      this.setSearchLoaded()
      throw e
    }
  }

  search = async (searchApi) => {
    this.setSearchLoading()
    const { searchString } = this.state
    try {
      const searchResults = await searchApi(searchString)
      this.setSearchLoaded()
      this.updateSearchResults(searchResults)
    } catch (e) {
      this.setSearchLoaded()
      this.updateSearchResults([])
      throw e
    }
  }

  getInputProps = () => {
    const { resultParser } = this.props
    const { selected, searchString, loading } = this.state
    return {
      value: selected ? resultParser(selected) : searchString,
      onChange: (_, value) => this.updateSearchString(value),
      loading,
      onKeyDown: this.onInputKeyDown,
      hasReset: !isObjectEmpty(selected),
      reset: this.reset
    }
  }

  getDropdownProps = () => {
    const { onSearchResultsClosed } = this.props
    const { showDropdown, searchResults } = this.state
    return {
      show: showDropdown,
      onOpen: this.showDropdown,
      onClose: this.closeDropdown,
      onClosed: onSearchResultsClosed,
      controlled: true,
      onOutsideClick: () => showDropdown && this.setSearchResultSelected(searchResults[0])
    }
  }

  getResultItemProps = (item, index) => {
    const { resultParser } = this.props
    const { highlightedIndex, searchString } = this.state
    return {
      searchString,
      value: resultParser(item),
      isHighlight: highlightedIndex === index,
      onClick: () => this.setSearchResultSelected(item)
    }
  }

  setHighlightIndex = (highlightedIndex) => {
    const { resultParser } = this.props
    const { searchResults, savedSearchString } = this.state
    this.setState({ highlightedIndex })
    const resultItem = searchResults[highlightedIndex]
    if (resultItem) {
      this.updateSearchString(resultParser(resultItem), false)
    } else {
      this.updateSearchString(savedSearchString, false)
    }
  }

  selectHighlightIndex = () => {
    const { searchResults, highlightedIndex } = this.state
    const index = highlightedIndex !== -1 ? highlightedIndex : 0
    this.setSearchResultSelected(searchResults[index])
  }

  onInputKeyDown = (e) => {
    const { showDropdown, highlightedIndex, searchResults = [] } = this.state
    if (!showDropdown) {
      trapEnterKey(e)
      return
    }
    switch (e.keyCode) {
      case keyCodes.ARROW_UP:
        this.setHighlightIndex(rotateIndex(highlightedIndex, -1, searchResults.length))
        e.preventDefault()
        break
      case keyCodes.ARROW_DOWN:
        this.setHighlightIndex(rotateIndex(highlightedIndex, 1, searchResults.length))
        e.preventDefault()
        break
      case keyCodes.TAB:
      case keyCodes.ENTER:
        this.selectHighlightIndex()
        e.preventDefault()
        break
      case keyCodes.ESC:
        this.closeDropdown()
        e.stopPropagation()
        break
    }
  }

  render () {
    return this.props.children(this.state)
  }
}

export default SearchInput
